import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Save } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import api from '@/lib/api';

export function GoogleOAuthTab() {
  const { t } = useI18n();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/auth/google/settings').then(r => setSettings(r.data)).catch(() => {
      setSettings({ client_id: '', client_secret: '', enabled: false });
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/auth/google/settings', settings);
      toast.success(t('cms.save_ok'));
    } catch {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>;

  return (
    <Card className="border" data-testid="google-oauth-tab">
      <CardHeader>
        <CardTitle className="text-base">{t('cms.google_title')}</CardTitle>
        <CardDescription>{t('cms.google_desc')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
          <p className="font-medium mb-1">{t('cms.google_howto_title')}</p>
          <ol className="list-decimal list-inside space-y-1 text-xs">
            <li>{t('cms.google_step1')}</li>
            <li>{t('cms.google_step2')}</li>
            <li>{t('cms.google_step3')}</li>
            <li>{t('cms.google_step4')}</li>
          </ol>
        </div>

        <div className="space-y-2">
          <Label>Client ID</Label>
          <Input
            value={settings.client_id || ''}
            onChange={e => setSettings({ ...settings, client_id: e.target.value })}
            placeholder="123456789.apps.googleusercontent.com"
            data-testid="google-client-id"
          />
        </div>

        <div className="space-y-2">
          <Label>Client Secret</Label>
          <Input
            type="password"
            value={settings.client_secret || ''}
            onChange={e => setSettings({ ...settings, client_secret: e.target.value })}
            placeholder="GOCSPX-..."
            data-testid="google-client-secret"
          />
        </div>

        <div className="p-3 rounded-lg bg-muted/50 text-sm">
          <p className="font-medium mb-1">{t('cms.google_redirect_title')}</p>
          <code className="text-xs bg-background px-2 py-1 rounded border block mt-1 break-all" data-testid="google-redirect-uri">
            {window.location.origin}/api/auth/google/callback
          </code>
          <p className="text-xs text-muted-foreground mt-1">{t('cms.google_redirect_help')}</p>
        </div>

        <Button className="bg-[#007FFF] hover:bg-[#0066CC]" onClick={handleSave} disabled={saving} data-testid="google-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {t('cms.save')}
        </Button>
      </CardContent>
    </Card>
  );
}
